// Quién es el usuario en la sesión, tal como el mock lo muestra en el pie del sidebar y en el
// topbar: su ROL y la tienda donde opera, nunca su correo.
import type { RoleAssignmentInfoLocal } from '@/api/types/MeLocal';
import type { Scope } from '@/api/types/permissions';

/** Alcances de más específico a más amplio: el primero que aparece es el lugar donde opera. */
const SCOPE_SPECIFICITY: Scope[] = ['LEVEL_3', 'LEVEL_2', 'LEVEL_1', 'TENANT', 'PLATFORM'];

/** Alcance comodín: no nombra ninguna tienda, así que no sirve como etiqueta. */
const WILDCARD_SCOPE_REF = '*';

/** Un alcance que este front no conoce va al final: `indexOf` daría -1 y ganaría como el más específico. */
function specificity(scope: Scope): number {
    const index = SCOPE_SPECIFICITY.indexOf(scope);
    return index === -1 ? SCOPE_SPECIFICITY.length : index;
}

export interface SessionIdentity {
    /** Nombre del rol asignado (p.ej. "Vendedor"). */
    roleName: string | null;
    /** Nombre legible del destino; si el perfil no lo trae, su id. */
    scopeLabel: string | null;
}

/**
 * Deriva el rol y la tienda de las asignaciones del perfil. Gana la del alcance del portal actual;
 * sin portal o sin asignación a ese alcance, la más específica.
 */
export function resolveSessionIdentity(
    assignments: readonly RoleAssignmentInfoLocal[],
    portalScope?: Scope | null,
): SessionIdentity {
    if (assignments.length === 0) return { roleName: null, scopeLabel: null };

    const portalMatch = portalScope ? assignments.find((a) => a.scope === portalScope) : undefined;
    const [primary] = portalMatch
        ? [portalMatch]
        : [...assignments].sort((a, b) => specificity(a.scope) - specificity(b.scope));
    const scopeRef = primary.scopeRef === WILDCARD_SCOPE_REF ? null : primary.scopeRef;
    return {
        roleName: primary.roleName || null,
        scopeLabel: primary.scopeRefName ?? scopeRef,
    };
}
